import React, { useEffect, useState, useCallback } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'

import { itemsActions } from '../../core/items'
import {
  brandsActions,
  Brand,
  getDefaultBrandCodeByRole,
} from '../../core/brands'
import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { Item } from '../../core/items'
import { strings } from '../components/items/items-content'
import styles from '../styles/modules/items/items.module.scss'
import ItemTable from '../components/items/item-table'
import { BrandFilter, BrandCode } from '../components/common'
import {
  roleType,
  Permission,
  hasPermission,
  isSbjUser,
} from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'

interface Props {
  hotItems: Item[]
  iceItems: Item[]
  isLoading: boolean
  role: roleType
  brands: Brand[]
  fetchItems: (brandCode?: string | null) => void
  fetchItemsEvery: () => void
  fetchItemsEveryFinished: () => void
  setCurrentBrandCode: (brandCode: string | null) => void
  changeItemsIndex: (ids: number[]) => void
  setHotItems: (hotItems: Item[]) => void
  setIceItems: (iceItems: Item[]) => void
  fetchBrands: () => void
}

const ItemsScreen: React.FC<Props> = ({
  hotItems,
  iceItems,
  isLoading,
  role,
  brands,
  fetchItems,
  fetchItemsEvery,
  fetchItemsEveryFinished,
  setCurrentBrandCode,
  changeItemsIndex,
  setHotItems,
  setIceItems,
  fetchBrands,
}) => {
  const defaultBrandCode = getDefaultBrandCodeByRole(isSbjUser(role))
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(
    defaultBrandCode
  )
  const [filterTriggered, setFilterTriggered] = useState(false)
  const isAdmin = role === roleType.ADMIN
  const canUpdateItem = hasPermission(role, Permission.ITEM_UPDATE)

  useEffect(() => {
    setCurrentBrandCode(defaultBrandCode)
    fetchItems(defaultBrandCode)
    fetchBrands()
    // 初期化処理のため、コンポーネントマウント時に一度だけ実行される必要がある
    // 依存配列に関数を追加すると、フィルタリング後に再度この処理が実行されてしまう
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchItemsEvery()
    return () => {
      fetchItemsEveryFinished()
    }
    // 継続取得処理の開始・終了制御のため、コンポーネントマウント時に一度だけ実行される必要がある
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBrandChange = useCallback((brand: BrandCode) => {
    setSelectedBrand(brand)
  }, [])

  const handleFilterClick = useCallback(() => {
    setFilterTriggered(true)
    setCurrentBrandCode(selectedBrand)
    fetchItems(selectedBrand)
  }, [selectedBrand, setCurrentBrandCode, fetchItems])

  useEffect(() => {
    if (filterTriggered && !isLoading) {
      setFilterTriggered(false)
    }
  }, [filterTriggered, isLoading])

  return (
    <Layout location={'items'}>
      <Wrapper title={strings.title}>
        <div className={styles.filterContainer}>
          <BrandFilter
            currentBrand={selectedBrand}
            onBrandChange={handleBrandChange}
            onFilterClick={handleFilterClick}
            isAdminOnly={isAdmin}
            brands={brands}
            isLoading={isLoading && filterTriggered}
          />
        </div>
        <div className={styles.container}>
          <h3 className={styles.title}>{strings.hot}</h3>
          <ItemTable
            items={hotItems}
            changeItemsIndex={canUpdateItem ? changeItemsIndex : undefined}
            setItems={setHotItems}
          />
        </div>
        <div className={styles.container}>
          <h3 className={styles.title}>{strings.ice}</h3>
          <ItemTable
            items={iceItems}
            changeItemsIndex={canUpdateItem ? changeItemsIndex : undefined}
            setItems={setIceItems}
          />
        </div>
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  hotItems: state.items.hotItems,
  iceItems: state.items.iceItems,
  isLoading: state.items.isLoading,
  role: state.auth0.role,
  brands: state.brands.brands,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchItems: (brandCode?: string | null) =>
    dispatch(itemsActions.fetchItems(brandCode)),
  fetchItemsEvery: () => dispatch(itemsActions.fetchItemsEvery()),
  fetchItemsEveryFinished: () =>
    dispatch(itemsActions.fetchItemsEveryFinished()),
  changeItemsIndex: (ids: number[]) =>
    dispatch(itemsActions.changeItemsIndex(ids)),
  setHotItems: (hotItems: Item[]) =>
    dispatch(itemsActions.setHotItems(hotItems)),
  setIceItems: (iceItems: Item[]) =>
    dispatch(itemsActions.setIceItems(iceItems)),
  setCurrentBrandCode: (brandCode: string | null) =>
    dispatch(itemsActions.setCurrentBrandCode(brandCode)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedItemsScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(ItemsScreen)

export default withPermissionCheck(ConnectedItemsScreen, {
  requiredPermission: Permission.ITEM_READ,
  redirectTo: '/404',
})
